import {
  ArrowRightOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FormOutlined,
  QuestionCircleOutlined,
  RiseOutlined,
  SoundOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { localeTag } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';
import { formatPercent } from '../utils/formatPercent';

const { Title, Text } = Typography;

interface EventRecord {
  id: string;
  title: string;
  slug: string;
  startDate: string;
  endDate: string;
  location?: string;
  isActive?: boolean;
  config?: { pricing?: { currency?: string } };
  _count?: { Ticket?: number; Session?: number; Speaker?: number };
}

interface EventStats {
  ticketsTotal: number;
  ticketsSold: number;
  ticketsCheckedIn: number;
  ticketsCapacity: number;
  registrationsPending: number;
  registrationsTotal: number;
  currency: string;
  revenueByCurrency: Record<string, number>;
  checkInRate: number;
}

interface EventWithStats extends EventRecord {
  stats?: EventStats;
  statsLoading?: boolean;
  statsError?: boolean;
}

interface PlatformStats {
  sessions: number;
  speakers: number;
  sponsors: number;
  faqs: number;
  documents: number;
  notifications: number;
}

type EventBucket = 'current' | 'upcoming' | 'past';

// Prisma types both dates non-null, so this only matters for a malformed
// payload — which is exactly when it matters. Presence is checked before
// validity because `dayjs(undefined)` is *valid*: it means now, so a missing
// endDate would otherwise pass an `isValid()` guard and print a range ending
// today.
const eventDateRange = (event: EventRecord): { start: Dayjs; end: Dayjs } | null => {
  if (!(event.startDate && event.endDate)) return null;

  const start = dayjs(event.startDate);
  const end = dayjs(event.endDate);

  return start.isValid() && end.isValid() ? { start, end } : null;
};

const bucketFor = (event: EventRecord): EventBucket => {
  const range = eventDateRange(event);

  // A row we cannot place on the timeline must not render as a confident green
  // "in progress" card carrying '—' for its dates — an organizer would believe
  // it — and must not pull stats for itself either. 'past' drops it out of the
  // active section instead.
  if (!range) return 'past';

  const now = dayjs();
  if (range.end.isBefore(now, 'day')) return 'past';
  if (range.start.isAfter(now, 'day')) return 'upcoming';
  return 'current';
};

// Dates and money follow the admin's language, not the browser default —
// dayjs carries one global locale, set from the panel's language in
// `i18n/config.ts`, so Intl is used here to format independently of it.
// `formatRange` collapses what the shared parts of a date range are on its own,
// per locale — a single day prints once, a same-month range shares the month.
// Doing that by hand meant one set of separator rules, which only ever fitted
// Hungarian, and it dropped the closing year on a range crossing New Year
// ("2026. dec. 30. – jan. 2.", no 2027).
const formatEventDates = (event: EventRecord, language: string): string => {
  const range = eventDateRange(event);

  // An unparseable date used to render as the string "Invalid Date" and stop
  // there; `formatRange` throws a RangeError instead, and the panel has no
  // ErrorBoundary — one bad row would blank the whole screen.
  if (!range) {
    return '—';
  }

  return new Intl.DateTimeFormat(localeTag(language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).formatRange(range.start.toDate(), range.end.toDate());
};

const formatCurrencyValue = (value: number, currency: string, language: string): string =>
  new Intl.NumberFormat(localeTag(language), {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 2,
  }).format(value || 0);

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<EventWithStats[]>([]);
  const [platform, setPlatform] = useState<PlatformStats>({
    sessions: 0,
    speakers: 0,
    sponsors: 0,
    faqs: 0,
    documents: 0,
    notifications: 0,
  });

  useEffect(() => {
    void fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1) Hozzuk az összes eseményt — innen tudjuk melyik aktív / közelgő /
      //    lezárult, és melyikhez kell event-stats kártya.
      const eventsRes = await api.get('/events', {
        params: { limit: 100 },
      });
      const eventList: EventRecord[] = eventsRes.data?.data || eventsRes.data || [];

      // 2) Platform-szintű engagement adatokat háttérben hozzuk —
      //    a per-event kártyákhoz nem kellenek, így ne blokkolják.
      const platformPromise = Promise.allSettled([
        api.get('/sessions'),
        api.get('/speakers'),
        api.get('/sponsors'),
        api.get('/faqs'),
        api.get('/documents'),
        api.get('/notifications/stats'),
      ]).then((results) => {
        const [s, sp, spo, f, d, n] = results;
        const len = (r: PromiseSettledResult<any>): number => {
          if (r.status !== 'fulfilled') return 0;
          const v = r.value.data;
          if (Array.isArray(v)) return v.length;
          if (Array.isArray(v?.data)) return v.data.length;
          return 0;
        };
        setPlatform({
          sessions: len(s),
          speakers: len(sp),
          sponsors: len(spo),
          faqs: len(f),
          documents: len(d),
          notifications: n.status === 'fulfilled' ? n.value.data?.sent || 0 : 0,
        });
      });

      // 3) A nem-lezárult eseményekre kérünk részletes statisztikát.
      //    A lezárultaknak az event._count.Ticket is elég a kompakt listában.
      const sortedEvents = [...eventList].sort(
        (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );

      // Render the events with placeholder stats — a kártyák azonnal
      // megjelennek loading state-tel.
      setEvents(sortedEvents.map((e) => ({ ...e, statsLoading: true })));
      setLoading(false);

      // Fetch stats per active/upcoming event in parallel.
      const liveEvents = sortedEvents.filter((e) => bucketFor(e) !== 'past');

      const statsResults = await Promise.allSettled(
        liveEvents.map((e) => api.get(`/events/${e.id}/stats`))
      );

      setEvents((current) =>
        current.map((evt) => {
          const idx = liveEvents.findIndex((e) => e.id === evt.id);
          if (idx < 0) {
            // past — nincs külön stats fetch, csak _count.Ticket-ből
            return { ...evt, statsLoading: false };
          }
          const r = statsResults[idx];
          if (r.status === 'fulfilled') {
            const d = r.value.data;
            return {
              ...evt,
              statsLoading: false,
              stats: {
                ticketsTotal: d.ticketsTotal ?? d.attendees ?? 0,
                ticketsSold: d.ticketsSold ?? 0,
                ticketsCheckedIn: d.ticketsCheckedIn ?? d.checkedIn ?? 0,
                ticketsCapacity: d.ticketsCapacity ?? 0,
                registrationsPending: d.registrationsPending ?? 0,
                registrationsTotal: d.registrationsTotal ?? 0,
                currency: d.currency ?? 'EUR',
                revenueByCurrency: d.revenueByCurrency ?? {
                  [d.currency ?? 'EUR']: d.revenue ?? 0,
                },
                checkInRate: d.checkInRate ?? 0,
              },
            };
          }
          return { ...evt, statsLoading: false, statsError: true };
        })
      );

      await platformPromise;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch dashboard data:', err);
      setError(t('dashboard.loadError'));
      setLoading(false);
    }
  };

  const greeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.greetingMorning');
    if (hour < 17) return t('dashboard.greetingAfternoon');
    return t('dashboard.greetingEvening');
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 400,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={t('common.error')} description={error} type="error" showIcon />;
  }

  const currentAndUpcoming = events.filter((e) => bucketFor(e) !== 'past');
  const pastEvents = events.filter((e) => bucketFor(e) === 'past');

  return (
    <div>
      <Title level={2} style={{ marginBottom: 4 }}>
        {greeting()}, {user?.firstName}! 👋
      </Title>
      <Text type="secondary">{t('dashboard.subtitle')}</Text>

      {/* Per-event cards — aktív és közelgő események */}
      <div style={{ marginTop: 24 }}>
        <Title level={4} style={{ marginBottom: 12 }}>
          {t('dashboard.activeAndUpcoming', { n: currentAndUpcoming.length })}
        </Title>
        {currentAndUpcoming.length === 0 ? (
          <Card>
            <Empty
              description={t('dashboard.noActiveEvents')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {currentAndUpcoming.map((evt) => (
              <Col key={evt.id} xs={24} lg={12}>
                <EventCard event={evt} onOpen={() => navigate(`/events/${evt.id}`)} />
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Lezárult események — kompakt lista */}
      {pastEvents.length > 0 && (
        <Card
          style={{ marginTop: 24 }}
          title={t('dashboard.pastEvents', { n: pastEvents.length })}
          extra={<a href="/events">{t('common.all')}</a>}
        >
          <List
            dataSource={pastEvents.slice(0, 5)}
            renderItem={(evt) => (
              <List.Item
                actions={[
                  <Tag key="past">{t('dashboard.past')}</Tag>,
                  <a key="open" href={`/events/${evt.id}`}>
                    {t('dashboard.open')}
                  </a>,
                ]}
              >
                <List.Item.Meta
                  avatar={<CalendarOutlined style={{ fontSize: 18 }} />}
                  title={evt.title}
                  description={
                    <Space size="middle">
                      <Text type="secondary">{formatEventDates(evt, i18n.language)}</Text>
                      <Text type="secondary">
                        {t('dashboard.ticketsCount', {
                          count: evt._count?.Ticket ?? 0,
                        })}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Platform-szintű aktivitás — kisebb hangsúllyal */}
      <Card style={{ marginTop: 24 }} title={t('dashboard.platformActivity')}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title={t('dashboard.sessions')}
              value={platform.sessions}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title={t('dashboard.speakers')}
              value={platform.speakers}
              prefix={<SoundOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title={t('dashboard.sponsors')}
              value={platform.sponsors}
              prefix={<TrophyOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title={t('dashboard.faqs')}
              value={platform.faqs}
              prefix={<QuestionCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title={t('dashboard.documents')}
              value={platform.documents}
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title={t('dashboard.notifications')}
              value={platform.notifications}
              prefix={<BellOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* Gyors műveletek */}
      <Card style={{ marginTop: 24 }} title={t('dashboard.quickActions')}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={6}>
            <QuickAction
              icon={<CalendarOutlined style={{ fontSize: 22, color: '#1890ff' }} />}
              title={t('dashboard.quickNewEvent')}
              description={t('dashboard.quickNewEventDesc')}
              onClick={() => navigate('/events')}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <QuickAction
              icon={<BellOutlined style={{ fontSize: 22, color: '#52c41a' }} />}
              title={t('dashboard.quickNotification')}
              description={t('dashboard.quickNotificationDesc')}
              onClick={() => navigate('/notifications')}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <QuickAction
              icon={<CheckCircleOutlined style={{ fontSize: 22, color: '#faad14' }} />}
              title={t('dashboard.quickCheckIn')}
              description={t('dashboard.quickCheckInDesc')}
              onClick={() => navigate('/checkin')}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <QuickAction
              icon={<RiseOutlined style={{ fontSize: 22, color: '#722ed1' }} />}
              title={t('dashboard.quickAnalytics')}
              description={t('dashboard.quickAnalyticsDesc')}
              onClick={() => navigate('/analytics')}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

interface QuickActionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

const QuickAction: React.FC<QuickActionProps> = ({ icon, title, description, onClick }) => (
  <Card hoverable onClick={onClick} style={{ cursor: 'pointer' }}>
    <Space align="start">
      <div>{icon}</div>
      <div>
        <Text strong>{title}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {description}
        </Text>
      </div>
    </Space>
  </Card>
);

interface EventCardProps {
  event: EventWithStats;
  onOpen: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onOpen }) => {
  const { t, i18n } = useTranslation();
  const bucket = bucketFor(event);
  const stats = event.stats;

  const statusTag =
    bucket === 'current' ? (
      <Tag color="green">{t('dashboard.statusInProgress')}</Tag>
    ) : (
      <Tag color="blue">{t('dashboard.statusUpcoming')}</Tag>
    );

  const daysUntil =
    bucket === 'upcoming'
      ? dayjs(event.startDate).startOf('day').diff(dayjs().startOf('day'), 'day')
      : null;

  const soldPercent =
    stats && stats.ticketsCapacity > 0
      ? Math.min(100, Math.round((stats.ticketsSold / stats.ticketsCapacity) * 100))
      : 0;

  const revenueEntries = stats
    ? Object.entries(stats.revenueByCurrency).filter(([, v]) => v > 0)
    : [];

  return (
    <Card
      title={
        <Space wrap>
          <CalendarOutlined />
          <span>{event.title}</span>
          {statusTag}
          {daysUntil !== null && daysUntil <= 14 && daysUntil >= 0 && (
            <Tag color="orange">
              {daysUntil === 0
                ? t('dashboard.startsToday')
                : t('dashboard.startsInDays', { count: daysUntil })}
            </Tag>
          )}
        </Space>
      }
      extra={
        <Button type="link" onClick={onOpen} icon={<ArrowRightOutlined />}>
          {t('dashboard.open')}
        </Button>
      }
    >
      <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
        <Text type="secondary">
          <ClockCircleOutlined /> {formatEventDates(event, i18n.language)}
        </Text>
        {event.location && (
          <Text type="secondary">
            <EnvironmentOutlined /> {event.location}
          </Text>
        )}
      </Space>

      {event.statsLoading ? (
        <Spin />
      ) : event.statsError || !stats ? (
        <Text type="secondary">{t('dashboard.statsUnavailable')}</Text>
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('dashboard.ticketsSold')}
                value={stats.ticketsSold}
                suffix={stats.ticketsCapacity > 0 ? `/ ${stats.ticketsCapacity}` : undefined}
                valueStyle={{ color: '#52c41a', fontSize: 18 }}
                prefix={<TagsOutlined />}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('dashboard.checkedIn')}
                value={stats.ticketsCheckedIn}
                valueStyle={{ color: '#1890ff', fontSize: 18 }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('dashboard.registrationsPending')}
                value={stats.registrationsPending}
                valueStyle={{ color: '#fa8c16', fontSize: 18 }}
                prefix={<FormOutlined />}
              />
            </Col>
            <Col xs={12} sm={6}>
              <div>
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                  {t('dashboard.revenue')}
                </div>
                {revenueEntries.length === 0 ? (
                  <Text strong style={{ fontSize: 18, color: '#722ed1' }}>
                    {formatCurrencyValue(0, stats.currency, i18n.language)}
                  </Text>
                ) : (
                  revenueEntries.map(([ccy, val]) => (
                    <div
                      key={ccy}
                      style={{
                        fontSize: 18,
                        color: '#722ed1',
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.3,
                      }}
                    >
                      {formatCurrencyValue(val, ccy, i18n.language)}
                    </div>
                  ))
                )}
              </div>
            </Col>
          </Row>

          {stats.ticketsCapacity > 0 && (
            <div style={{ marginTop: 12 }}>
              <Progress percent={soldPercent} strokeColor="#52c41a" format={(p) => `${p}%`} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('dashboard.occupancy', {
                  sold: stats.ticketsSold,
                  capacity: stats.ticketsCapacity,
                })}
              </Text>
            </div>
          )}

          {stats.ticketsSold > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('dashboard.checkInRate')}{' '}
                <Text strong>{formatPercent(stats.checkInRate, i18n.language)}</Text>
                {' · '}
                <TeamOutlined />{' '}
                {t('dashboard.registrationsCount', {
                  count: stats.registrationsTotal,
                })}
              </Text>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default Dashboard;
